import { useMemo, useState } from 'react';
import type { PLSummary, ReportPeriod, TaxEstimate } from '@eriko/shared-types';
import type { CustomRange } from './types';

function currentQuarter(now: Date = new Date()): 1 | 2 | 3 | 4 {
  return (Math.floor(now.getUTCMonth() / 3) + 1) as 1 | 2 | 3 | 4;
}

function quarterKey(q: 1 | 2 | 3 | 4): ReportPeriod {
  return (`q${q}` as ReportPeriod);
}

export const EMPTY_SUMMARY: PLSummary = {
  revenue: 0,
  expenses: 0,
  netProfit: 0,
  revenueDelta: { label: '—' },
  expensesDelta: { label: '—' },
  netProfitDelta: { label: '—' },
};

export const EMPTY_TAX: TaxEstimate = {
  quarter: '',
  headline: 'Set aside for estimated taxes',
  amount: 0,
  noteHighlights: ['$0 net profit', ''],
};

export interface PeriodState {
  period: ReportPeriod;
  setPeriod: (next: ReportPeriod) => void;
  customRange: CustomRange | null;
  applyCustomRange: (range: CustomRange) => void;
  periodLabels: Record<ReportPeriod, string>;
  visiblePeriods: ReportPeriod[];
}

export function usePeriodState(initial: ReportPeriod = 'this_month'): PeriodState {
  const [period, setPeriodState] = useState<ReportPeriod>(initial);
  const [customRange, setCustomRange] = useState<CustomRange | null>(null);

  const { visiblePeriods, periodLabels } = useMemo(() => {
    const now = new Date();
    const q = currentQuarter(now);
    const qKey = quarterKey(q);
    const qLabel = `Q${q} ${now.getUTCFullYear()}`;

    const labels: Record<ReportPeriod, string> = {
      this_week: 'This week',
      this_month: 'This month',
      last_month: 'Last month',
      q1: 'Q1',
      q2: 'Q2',
      q3: 'Q3',
      q4: 'Q4',
      ytd: 'YTD',
      last_year: 'Last year',
      custom: 'Custom',
    };
    labels[qKey] = qLabel;

    const visible: ReportPeriod[] = [
      'this_week',
      'this_month',
      qKey,
      'ytd',
      'last_year',
      'custom',
    ];

    return { visiblePeriods: visible, periodLabels: labels };
  }, []);

  function setPeriod(next: ReportPeriod): void {
    setPeriodState(next);
  }

  function applyCustomRange(range: CustomRange): void {
    setCustomRange(range);
    setPeriodState('custom');
  }

  return {
    period,
    setPeriod,
    customRange,
    applyCustomRange,
    periodLabels,
    visiblePeriods,
  };
}

/**
 * Build the "VOL. III · ISSUE NN · PERIOD" masthead string. The issue number
 * advances at "quarter × 2" so the label visibly changes twice a quarter —
 * purely stylistic.
 */
export function buildMastheadVolume(periodLabel: string, now: Date = new Date()): string {
  const quarter = Math.ceil((now.getMonth() + 1) / 3);
  const issueNumber = String(quarter * 2).padStart(2, '0');
  return `VOL. III · ISSUE ${issueNumber} · ${periodLabel.toUpperCase()}`;
}
